import { getOfferAdminFee, getOfferPrice, isSubscription } from "../index/utils";
import { addMonths, startOfMonth, endOfMonth, differenceInDays, getDaysInMonth } from "date-fns";

export default function useSummaryCalculator(startDate: Date, offer: {}){
	const endOfStartMonth = endOfMonth(startDate);
	const daysInStartMonth = getDaysInMonth(startDate);
	/*
		Resamania bills the start month INCLUSIVE of the start day: a start on the 1st of
		a 31-day month is charged the full 31/31, and a start on the last day is charged
		1/31 (not 0). differenceInDays counts whole days between the two instants (start of
		the start day -> end of the month), which is one short of that inclusive span, so
		the start day is added back with `+ 1`.

		Verified against the API across start dates (sandbox sale.totalTI to the penny,
		£45/mo offer, Oct = 31 days, £30 joining):
		  -15 (pro-rata only):  1 Oct -> 31d (£75.00), 15 Oct -> 17d (£54.68), 31 Oct -> 1d (£31.45)
		  +15 (prepaid month):  the SAME inclusive pro-rata PLUS one flat month (see below):
		                        16 Oct -> £98.23, 20 Oct -> £92.42, 31 Oct -> £76.45

		An earlier revision stripped the start day (no `+ 1`) to match a then-current API;
		the API now bills inclusively, so without the `+ 1` every pro-rata is a day short
		and a last-day-of-month start collapses the whole first month to £0.
	*/
	const inclusiveDaysInStartMonth = differenceInDays(endOfStartMonth, startDate) + 1;
	const firstMonthUsedRatio = inclusiveDaysInStartMonth / daysInStartMonth;

	const subscription = isSubscription(offer);

	/*
		A "+15" offer — one sold in the second half of the month — is charged a whole
		extra month up front. The 14-day direct-debit cool-off means the first debit
		cannot fall on the 1st of next month, so Resamania takes next month with the
		initial payment and starts the debit the month after. isFirstMonthPayed is the
		flag for exactly this; a "-15" offer (first half) has it false and is billed pro
		rata only. Confirmed against a sale: a 17th-of-month +15 start charged pro-rata +
		a full month + the joining fee (totalTI £96.00), not pro-rata + joining (£49.50).
		Without this, the pre-payment estimate understated the first payment by a month.
	*/
	const firstMonthPrepaid = subscription && (offer as { isFirstMonthPayed?: boolean })?.isFirstMonthPayed === true;

	const monthlyFee = getOfferPrice(offer);
	// The joining fee, whether the offer is a subscription or a one-off product —
	// getOfferAdminFee returns null when there is none.
	const adminFee = getOfferAdminFee(offer) ?? 0;

	/*
		The start month is charged at its daily rate (monthlyFee / daysInStartMonth) for the
		inclusive span from the start day to the end of the start month. A +15 offer prepays
		one WHOLE extra month ON TOP of that, at the flat monthlyFee — the 14-day DD cool-off
		means the first debit cannot fall on the 1st of next month, so Resamania takes next
		month with the initial payment and starts the debit the month after.

		Verified against the API to the penny (sandbox, £45/mo + £30 joining, Oct):
		  +15 20 Oct = 12 inclusive days × 45/31 (£17.42) + a flat £45 month + £30 = £92.42.
		The prepaid month is a FLAT monthlyFee, not the start-month daily rate extended into
		next month. A one-off product is bought outright and is never pro-rated.
	*/
	const dailyRate = daysInStartMonth > 0 ? monthlyFee / daysInStartMonth : 0;
	const prorataStartMonth = inclusiveDaysInStartMonth * dailyRate;
	const membershipDueToday = subscription
		? (firstMonthPrepaid ? prorataStartMonth + monthlyFee : prorataStartMonth)
		: monthlyFee;
	const amountTotal = membershipDueToday + adminFee;

	// Regular billing starts the 1st after the period already paid for: the next month
	// for a normal offer, the month after that when the first month was prepaid.
	const directDebitFirstDay = startOfMonth(addMonths(startDate, firstMonthPrepaid ? 2 : 1));

	return {
		directDebitFirstDay: directDebitFirstDay,
		endOfStartMonth: endOfStartMonth,
		daysInStartMonth: daysInStartMonth,
		firstMonthUsedRatio: firstMonthUsedRatio,

		monthlyFee: monthlyFee,
		adminFee: adminFee,

		firstMonthPrepaid: firstMonthPrepaid,
		membershipDueToday: membershipDueToday,
		amountTotal: amountTotal,
	}
}
